import { AlertTriangle } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';

export function LegalShell({ title, updated, flag, children }) {
  return (
    <div>
      <section className="bg-[#10282C] pt-36 pb-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <h1 className="font-display font-bold text-white text-4xl sm:text-5xl tracking-tight">{title}</h1>
          <p className="mt-4 text-[13px] font-mono text-white/40">Last updated: {updated}</p>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="rounded-xl bg-[#FD584F]/8 border border-[#FD584F]/25 px-5 py-4 flex items-start gap-3 mb-10" data-testid="legal-review-flag">
            <AlertTriangle size={17} className="text-[#c23a32] shrink-0 mt-0.5" />
            <p className="text-[13px] text-[#8a2a24] leading-relaxed">{flag}</p>
          </div>
          <div className="space-y-9 text-[15px] text-[#10282C]/70 leading-relaxed [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-[#10282C] [&_h2]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5">
            {children}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function Privacy() {
  const { t } = useLang();
  return (
    <LegalShell title="Privacy Policy" updated="June 2026" flag={t('legal.reviewFlag')}>
      <div>
        <h2>1. Who we are</h2>
        <p>Gyeno is a product of DulipTech ("we", "us"). This policy explains how we collect, use, and protect personal data when you use the Gyeno website and platform. For data requests, contact us via the Enterprise/Contact page.</p>
      </div>
      <div>
        <h2>2. Data we collect</h2>
        <ul>
          <li><strong>Account data:</strong> name, business email, company name, country/region, password (stored as a salted hash).</li>
          <li><strong>Contact form data:</strong> name, email, company, job title, region, company size, Salesforce edition, use-case description.</li>
          <li><strong>Usage data:</strong> pages visited and interactions, collected only with your consent via the cookie banner.</li>
          <li><strong>Chat assistant data:</strong> messages (and, in voice mode, audio transcripts) you send to the site assistant, used to answer your questions.</li>
        </ul>
      </div>
      <div>
        <h2>3. Salesforce data handling</h2>
        <p>The Gyeno platform runs inside your Salesforce org and operates within your org's own permission model. We do not extract or store your Salesforce records on this marketing website. Platform-level data processing is governed by your platform agreement and order form.</p>
      </div>
      <div>
        <h2>4. Third-party LLM providers as sub-processors</h2>
        <p>To provide AI functionality, we use third-party large language model providers (Anthropic, OpenAI, Google) as sub-processors. Content you submit to AI features may be processed by these providers under their data processing terms. Enterprise customers may elect Bring-Your-Own-Key (BYOK) to route AI traffic through their own provider agreements.</p>
      </div>
      <div>
        <h2>5. Legal bases & GDPR (UK/EU visitors)</h2>
        <p>Where UK GDPR / GDPR applies, we process personal data on the bases of contract performance (account and platform delivery), legitimate interests (responding to enquiries, securing the service), and consent (analytics cookies, marketing). You have rights of access, rectification, erasure, restriction, portability, and objection, and the right to lodge a complaint with your supervisory authority (including the UK ICO).</p>
      </div>
      <div>
        <h2>6. Cookies & consent</h2>
        <p>Essential cookies keep the site functioning. Analytics cookies are set only after you choose "Accept all" in the consent banner. You can change your choice at any time by clearing site data in your browser.</p>
      </div>
      <div>
        <h2>7. Data retention & security</h2>
        <p>We retain account data for as long as your account is active, and contact submissions for as long as needed to respond and maintain business records. Passwords are hashed with bcrypt; transport is encrypted with TLS.</p>
      </div>
      <div>
        <h2>8. International transfers</h2>
        <p>We serve customers in the US, UK, Middle East, and APAC. Where data is transferred across borders, we rely on appropriate safeguards such as standard contractual clauses. [NEEDS BUSINESS INPUT: confirm hosting regions and transfer mechanisms.]</p>
      </div>
      <div>
        <h2>9. Contact for data requests</h2>
        <p>Submit any privacy or data request via the Enterprise/Contact page. We respond within 1 business day.</p>
      </div>
    </LegalShell>
  );
}
